/**
 * MobiliScheduler — the Scheduler shell (components/TerminPlan/Scheduler.tsx)
 * with the FullCalendar engine swapped for MobiliCalendar. Everything AROUND
 * the calendar is reused as-is: patient overview drawer (left), event shelf
 * (below the grid by default — see useShelfPlacement), cancellation/agent
 * hover sidebar (right) and the centralized SchedulerDialogs — they all talk
 * to the same stores/queries, so the new engine plugs into the existing
 * patient/shelf workflows without adapters.
 *
 * Deliberately NOT cloned (yet): route planning, timesheet and
 * single-therapist modes — those wrappers are Phase 6 in
 * docs/calendar-drop-in-replacement-plan.md and keep living in the old
 * Scheduler until the engine covers them. The header toolbar is a shadcn
 * REBUILD (MobiliHeaderToolbar), not the FullCalendar-coupled original.
 */
import { AutoFixHigh, Cancel } from "@mui/icons-material";
import { Box, Grid, Tab, Tabs } from "@mui/material";
import { observer } from "mobx-react";
import React, { useState } from "react";
import useStore from "../../helpers/useStore";
import CancellationDrawer from "../../molecules/Calendar/CancellationDrawer";
import EventShelf from "../../molecules/Calendar/EventShelf/EventShelf";
import CalendarDrawer from "../../molecules/Calendar/PatientDrawer/CalendarDrawer";
import HoverSidebar from "../../molecules/HoverSidebar";
import MobiliCalendarWithController from "../../molecules/MobiliCalendar/MobiliCalendarWithController";
import { MCStats } from "../../molecules/MobiliCalendar/model/types";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import { UserContext } from "../../stores/User/User.provider";
import { AgentDrawerContent } from "../TerminPlan/SchedulerAgent";
import SchedulerDialogs from "../TerminPlan/SchedulerDialogs";
import MobiliHeaderToolbar from "./MobiliHeaderToolbar";
import { useShelfPlacement } from "./useShelfPlacement";

const MobiliScheduler: React.FC = observer(() => {
  const patientDrawerWidth = 320;
  const rightSidebarWidth = 400;
  // Stornierungen is the working tab; the Agent (feature preview) is opt-in.
  const [rightTab, setRightTab] = useState<number>(0);
  const [stats, setStats] = useState<MCStats | null>(null);
  // Shelf above or below the grid — see useShelfPlacement for the why.
  const [shelfPlacement, setShelfPlacement] = useShelfPlacement();

  const CalendarStore = useStore(CalendarContext);
  const { selectedPatientId: psPId } = useStore(PatientsContext);
  const UserStore = useStore(UserContext);
  const canSeeFeaturePreview = UserStore.hasPermission("featurePreview");
  const {
    patientDrawerOpen: pdOpenState,
    cancellationDrawerOpen,
    setCancellationDrawerOpen,
    selectedPatientId: csPId,
  } = CalendarStore;
  // Debug-window prototype: shelf lives in the patient drawer instead of the
  // band. Legacy band stays the default; flip in the debug window (stores tab).
  const shelfInDrawer = CalendarStore.shelfInDrawer;
  if (!csPId && psPId) {
    // sync patient selection from patient store if needed (same as Scheduler)
    CalendarStore.setSelectedPatientId(psPId);
  }
  const selectedPatientId = csPId || psPId;

  const patientDrawerOpen = (selectedPatientId && pdOpenState) || false;

  return (
    <Grid container height={"100%"}>
      <Grid item width={patientDrawerOpen ? patientDrawerWidth : 0}>
        <CalendarDrawer
          open={patientDrawerOpen}
          drawerWidth={patientDrawerWidth}
          showOpenButton={true}
        />
      </Grid>
      <Grid
        item
        width={`calc(${cancellationDrawerOpen ? "100%" : "98%"} - ${
          patientDrawerOpen ? patientDrawerWidth : 0
        }px - ${cancellationDrawerOpen ? rightSidebarWidth : 0}px)`}
        sx={{ transition: "width 0.3s ease" }}
      >
        <Grid
          container
          direction="column"
          sx={{ height: "100%", flexWrap: "nowrap", alignItems: "stretch" }}
        >
          <Grid item sx={{ flexShrink: 0 }}>
            {/* Toolbar lives INSIDE the center column (right of the drawer),
                like the old Scheduler — the persistent drawer can never cover it. */}
            <MobiliHeaderToolbar
              stats={stats}
              shelfPlacement={shelfPlacement}
              onShelfPlacementChange={setShelfPlacement}
            />
          </Grid>
          {!shelfInDrawer && shelfPlacement === "above" && (
            <Grid item sx={{ flexShrink: 0, marginBottom: 1 }}>
              {/* unscheduled events for the selected patient */}
              <EventShelf placement="above" />
            </Grid>
          )}
          {/* minHeight:0 lets the grid actually give up height to the shelf
              instead of overflowing the column (flex items default to
              min-height:auto). */}
          <Grid item sx={{ flexGrow: 1, minHeight: 0, overflow: "hidden" }}>
            <MobiliCalendarWithController onStats={setStats} />
          </Grid>
          {!shelfInDrawer && shelfPlacement === "below" && (
            <Grid item sx={{ flexShrink: 0, marginTop: 1 }}>
              {/* Same shelf, below the grid: appearing/disappearing with the
                  selected patient now only moves the grid's bottom edge
                  instead of pushing the whole calendar down. */}
              <EventShelf placement="below" />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item sx={{ position: "relative" }}>
        <HoverSidebar
          onToggle={(open) => setCancellationDrawerOpen(open)}
          width={"" + rightSidebarWidth + "px"}
        >
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {canSeeFeaturePreview && (
              <Tabs
                value={rightTab}
                onChange={(_, v) => setRightTab(v)}
                variant="fullWidth"
                sx={{
                  minHeight: 36,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "& .MuiTab-root": { minHeight: 36, py: 0.5, fontSize: "0.75rem" },
                }}
              >
                <Tab icon={<Cancel sx={{ fontSize: 16 }} />} iconPosition="start" label="Stornierungen" />
                <Tab
                  icon={<AutoFixHigh sx={{ fontSize: 16 }} />}
                  iconPosition="start"
                  label="Agent"
                />
              </Tabs>
            )}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                display: !canSeeFeaturePreview || rightTab === 0 ? "block" : "none",
              }}
            >
              <CancellationDrawer />
            </Box>
            {canSeeFeaturePreview && (
              <Box sx={{ flex: 1, overflow: "hidden", display: rightTab === 1 ? "block" : "none" }}>
                <AgentDrawerContent patientId={selectedPatientId} />
              </Box>
            )}
          </Box>
        </HoverSidebar>
      </Grid>

      {/* Centralized dialog management (notes, CA responses, template drops) */}
      <SchedulerDialogs />
    </Grid>
  );
});
MobiliScheduler.displayName = "MobiliScheduler";

export default MobiliScheduler;
